import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MessageCircle, MapPin, Phone, Clock, Instagram, Mail } from 'lucide-react';

const WHATSAPP_NUMBER = "+919381690689";
const EMAIL = "fkhizra232@gmail.com";
const INSTAGRAM = "https://instagram.com/artcreativevallye_";

const ContactSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const handleWhatsAppContact = () => {
    const message = encodeURIComponent("Hi! I'm interested in your Islamic calligraphy artworks. Please share more details.");
    window.open(`https://wa.me/919381690689?text=${message}`, '_blank');
  };

  return (
    <section id="contact" className="py-24 relative" ref={sectionRef} data-testid="contact-section">
      {/* Background Glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#D4AF37]/5 blur-[150px] pointer-events-none" />

      <div className="section-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="font-manrope text-sm text-[#D4AF37] uppercase tracking-widest mb-4 block">
              Get in Touch
            </span>

            <h2 className="font-cinzel text-section-title text-gold-gradient mb-6">
              Let's Create Together
            </h2>

            <div className="w-20 h-0.5 bg-gradient-to-r from-[#AA8C2C] to-[#F4C430] mb-8" />

            <p className="font-manrope text-gray-300 leading-relaxed mb-8">
              Have a custom design in mind? Want to discuss a special commission? We'd love to hear from you. Reach out via WhatsApp for the quickest response.
            </p>

            {/* WhatsApp CTA */}
            <motion.button
              className="btn-whatsapp text-lg mb-10"
              onClick={handleWhatsAppContact}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="contact-whatsapp-btn"
            >
              <MessageCircle size={22} />
              Chat on WhatsApp
            </motion.button>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-[#D4AF37]" size={18} />
                </div>
                <div>
                  <h4 className="font-cinzel text-white mb-1">Location</h4>
                  <p className="font-manrope text-gray-400 text-sm">
                    Hyderabad, Telangana, India<br />
                    <span className="text-[#D4AF37]/80">Available for local delivery only</span>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="text-[#D4AF37]" size={18} />
                </div>
                <div>
                  <h4 className="font-cinzel text-white mb-1">WhatsApp</h4>
                  <p className="font-manrope text-gray-400 text-sm">+91 93816 90689</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="text-[#D4AF37]" size={18} />
                </div>
                <div>
                  <h4 className="font-cinzel text-white mb-1">Email</h4>
                  <p className="font-manrope text-gray-400 text-sm">{EMAIL}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="text-[#D4AF37]" size={18} />
                </div>
                <div>
                  <h4 className="font-cinzel text-white mb-1">Response Time</h4>
                  <p className="font-manrope text-gray-400 text-sm">
                    Usually within 2-4 hours<br />
                    <span className="text-gray-500">Mon - Sat, 10 AM - 8 PM</span>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Visual */}
          <motion.div
            className="flex items-center justify-center"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="relative">
              {/* Decorative Circle */}
              <div className="absolute inset-0 rounded-full border border-[#D4AF37]/20 animate-pulse" style={{ transform: 'scale(1.2)' }} />
              <div className="absolute inset-0 rounded-full border border-[#D4AF37]/10" style={{ transform: 'scale(1.4)' }} />

              {/* Main Circle Content */}
              <div className="glass rounded-full w-72 h-72 md:w-96 md:h-96 flex flex-col items-center justify-center text-center p-8">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.5 }}
                >
                  <p className="font-amiri text-3xl md:text-4xl text-[#D4AF37] mb-4">
                    بِسْمِ اللَّهِ
                  </p>
                  <p className="font-cinzel text-xl text-white mb-1">
                    Ethereal Calligraphy
                  </p>
                  <p className="font-manrope text-sm text-[#D4AF37]/70 mb-1">
                    Art Create Valley
                  </p>
                  <p className="font-manrope text-sm text-gray-400">
                    Hyderabad
                  </p>
                </motion.div>

                {/* Social Icons */}
                <motion.div
                  className="flex gap-4 mt-6"
                  initial={{ opacity: 0 }}
                  animate={isInView ? { opacity: 1 } : {}}
                  transition={{ delay: 0.7 }}
                >
                  <a
                    href={INSTAGRAM}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center text-gray-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-all"
                    data-testid="instagram-link"
                  >
                    <Instagram size={18} />
                  </a>
                  <a
                    href={`mailto:${EMAIL}`}
                    className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center text-gray-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-all"
                    data-testid="mail-link"
                  >
                    <Mail size={18} />
                  </a>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
